import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { config } from '@shakkerai/config';
import { initWorkflowData, mapWorkflowParam } from '../helpers/helpers';
import { scheduleTaskForUnit } from '../../../helpers/process';
import { commuityUrls } from '@starflow/app/constants/index';
import { useProjectState, useTempState, setTempState } from '@starflow/app/store';
import { useDebounceEffect } from 'ahooks';
import { Loader, Switch } from '@mantine/core';
import { calculatePower } from '@shakkerai/services/src/vip';
import { notifyError } from '@shakkerai/pro-components';
import { Icon } from '@shakkerai/components';
import powerIcon from '@shakkerai/pro-components/src/button/power.svg';
import { Node } from './Node';
import { MaskEditor } from './fields/MaskEditor';
import { updateUnitData } from '../../../helpers/flow';
import { translate } from '@shakkerai/util-i18n';
import { isOverseas } from '@shakkerai/pro-utils';
import { MemberSpeedBtn } from '@shakkerai/pro-components';

const communityUrl = commuityUrls[config.name] || commuityUrls.prod;

const WorkflowPanelContent = React.memo(({ unitId, unit }) => {
  const workflowData = unit.data;

  const setWorkflowData = useCallback(
    (partialData) => {
      updateUnitData(unitId, partialData);
    },
    [unitId],
  );

  const exclusiveWindow = useTempState('exclusiveWindow');

  const isMaskEditorOpened = exclusiveWindow === 'mask-editor';
  const [power, setPower] = useState(0);

  const allowGenerate = useMemo(() => {
    if (!workflowData) return false;
    if (!workflowData.initialized) return false;

    return !workflowData.nodes.some((node) => {
      return node.fields.some((field) => typeof field.value === 'undefined');
    });
  }, [workflowData]);

  const handleGenerate = useCallback(() => {
    scheduleTaskForUnit(unitId, { isFakeSize: true, extraParams: { width: 1024, height: 1024, count: 1 } });
  }, [unitId]);

  const handleRestore = useCallback(() => {
    if (workflowData.initialData) {
      setWorkflowData(workflowData.initialData);
    }
  }, [workflowData.initialData]);

  const handleViewWorkflow = useCallback(() => {
    window.open(`${communityUrl}${workflowData.modelUuid}`);
  }, [workflowData]);

  useEffect(() => {
    if (!workflowData.initialized) {
      if (!workflowData.rawData) {
        // TODO 找不到原始数据
        return;
      }

      initWorkflowData(workflowData.rawData)
        .then((data) => {
          data.initialData = JSON.parse(JSON.stringify(data));
          data.initialized = true;
          setWorkflowData(data);
        })
        .catch((error) => {
          notifyError(error.message || error);
        });
    }

    return () => {
      setTempState({
        shapesCache: [],
      });
    };
  }, [workflowData.initialized]);

  useDebounceEffect(
    () => {
      if (workflowData?.nodes) {
        // console.log('xxx',workflowData)
        mapWorkflowParam(workflowData).then((powerParams) => {
          calculatePower(powerParams)
            .then((calculateRes) => {
              if (Object.keys(calculateRes).includes('power')) {
                setPower(calculateRes.power);
              }
            })
            .catch(console.error);
        });
      }
    },
    [workflowData],
    { wait: 200 },
  );

  console.log(`[DEBUG] workflowData`, workflowData);

  if (!workflowData.initialized) {
    return (
      <div className="right-sidebar flex flex-col items-center justify-center w-[300px] fixed bottom-0 bg-white z-50 right-0 top-12">
        <Loader size={16} />
        <div className="mt-3 text-xs text-[#2d2d2d]">{translate('comfy_loading')}</div>
      </div>
    );
  }

  return (
    <>
      <header className="flex items-center px-4 h-[44px] border-b border-[#efefef] font-bold text-sm text-[#2d2d2d] font-grotesk">
        {workflowData?.title}
      </header>
      <div className="flex-1 overflow-auto tiny-scrollbar">
        {workflowData?.nodes.map((nodeData) => {
          return <Node key={nodeData.id} node={nodeData} workflow={workflowData} onChange={setWorkflowData} />;
        })}
      </div>
      <footer className="p-4 bg-[#fafafa]">
        <div className="flex items-center">
          <MemberSpeedBtn />
          <button
            disabled={!allowGenerate}
            onClick={handleGenerate}
            className="flex-1 ml-4 h-[38px] bg-[#2d2d2d] rounded-sm disabled:bg-[#999] text-white text-[13px] font-semibold"
          >
            <span className="ml-[12px] flex items-center justify-center">
              <span>{translate('generate')}</span>
              <img src={powerIcon} className="w-[18px] h-[18px] ml-[8px] inline" alt="" />
              {<span>{power}</span>}
            </span>
          </button>
        </div>
        <div className="flex items-center justify-between mt-3">
          <button onClick={handleRestore} className="flex items-center">
            <Icon className="shk-kuangjia-zhongzhi" fontSize={16} color="#7f7f7f" />
            <span className="ml-1 text-xs text-[#2d2d2d]">{translate('reset_generate_params')}</span>
          </button>
          {isOverseas ? null : (
            <button onClick={handleViewWorkflow} className="text-xs text-[#2d2d2d] font-semibold">
              {translate('inpaint_edit_community')}
            </button>
          )}
        </div>
      </footer>
      {isMaskEditorOpened && <MaskEditor />}
    </>
  );
});

const WorkflowPanelInner = React.memo(({ nodeId }) => {
  const node = useProjectState((state) => state.nodeMap[nodeId]);

  if (!node) return null;

  return <WorkflowPanelContent unitId={nodeId} unit={node.data} />;
});

export const WorkflowPanel = () => {
  const currentNodeId = useProjectState('currentNodeId');

  if (!currentNodeId) return null;

  const nodeType = currentNodeId.split('-')[1];

  if (nodeType !== 'workflow') return null;

  return <WorkflowPanelInner nodeId={currentNodeId} />;
};
